/** Account Progress slice — the reference apTrades(a) (script lines 1009–1038) and apModel() (script lines 2647–2921)
 *  ported verbatim: challenge hero, stat cards, drawdown panel, rule compliance, equity sparkline, predictive score panel,
 *  daily heatmap, best/worst, challenge trade log. Data: `c.data.progress.apAccounts` (AP_ACCOUNTS). */
import type { ApAccount } from '@pointfive/contracts';
import type { Ctx } from './ctx';
import type { ViewModel } from '@/screens/generated/view-model';

export interface ApTrade {
  sym: string;
  dir: 'buy' | 'sell';
  lots: number;
  pnl: number;
  comm: number;
  off: number;
  net: number;
}

/** Seeded challenge trade list for an account — bit-for-bit the reference apTrades(a). */
export function apTrades(a: ApAccount): ApTrade[] {
  let seed = a.seed;
  const rnd = () => (seed = (seed * 9301 + 49297) % 233280) / 233280;
  const syms = ['XAUUSD', 'EURUSD', 'GBPJPY', 'US30', 'NAS100'];
  const out: ApTrade[] = [];
  const gain = a.currentBalance - a.startingBalance;
  const n = Math.round(a.daysElapsed * 1.4);
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const win = rnd() < 0.58;
    const mag = a.startingBalance * (0.0016 + rnd() * 0.0042);
    const pnl = win ? mag : -mag * 0.72;
    const off = Math.round(rnd() * (a.daysElapsed - 1));
    out.push({
      sym: syms[Math.floor(rnd() * syms.length)]!, // rnd() < 1, so the index is always in range
      dir: rnd() < 0.55 ? 'buy' : 'sell',
      lots: +(0.02 + rnd() * 0.18).toFixed(2),
      pnl,
      comm: +(rnd() * 9).toFixed(2),
      off,
      net: 0,
    });
    sum += pnl;
  }
  // scale so NET closed P&L reconciles with the account's actual gain:
  // sum(pnl*k) - sum(comm) = gain  →  k = (gain + totalComm) / sum
  const totalComm = out.reduce((s, t) => s + t.comm, 0);
  const k = sum === 0 ? 1 : (gain + totalComm) / sum;
  out.forEach((t) => {
    t.pnl = t.pnl * k;
    t.net = t.pnl - t.comm;
  });
  return out.sort((x, y) => x.off - y.off);
}

interface GaugeTick {
  x1: string;
  y1: string;
  x2: string;
  y2: string;
  col: string;
  w: string;
  op: string;
}
type RuleStatus = 'passed' | 'ok' | 'progress' | 'warning' | 'failed';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function buildProgress(c: Ctx) {
  const S = c.S;
  const AP_ACCOUNTS = c.data.progress.apAccounts;
  // A trader with no linked challenge account still has to be shown the screen that says so. Throwing here meant
  // the whole view-model failed, so an account that had simply linked nothing replaced the entire product with an
  // error page. Every figure below is a zero and the labels name the absence — nothing is invented, least of all a
  // balance or a drawdown limit.
  const NO_ACCOUNT: ApAccount = {
    id: '',
    nickname: 'No challenge account linked',
    broker: '—',
    currency: 'USD',
    startingBalance: 0,
    currentBalance: 0,
    profitTarget: 0,
    dailyDrawdownLimit: 0,
    totalDrawdownLimit: 0,
    challengeDays: 0,
    daysElapsed: 0,
    daysRemaining: 0,
    challengeStart: '',
    seed: 0,
    score: 0,
    scoreLabel: '—',
    confidence: 0,
    summary: 'Link a funded or challenge account to track its progress here.',
    projected: '—',
    best: null,
    worst: null,
    strengths: [],
    risks: [],
  };
  const A = AP_ACCOUNTS.filter((a) => a.id === S.apAcct)[0] || AP_ACCOUNTS[0] || NO_ACCOUNT;
  const cur = (v: number) =>
    '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const sgn = (v: number) => (v >= 0 ? '+' : '−') + cur(v);
  const trades = apTrades(A);

  // challenge status
  const profitEarned = A.currentBalance - A.startingBalance;
  const profitPct = Math.min((profitEarned / A.profitTarget) * 100, 100);
  const gainPct = (profitEarned / A.startingBalance) * 100;
  const pacePct = Math.min((A.daysElapsed / A.challengeDays) * 100, 100);
  const isPassed = profitEarned >= A.profitTarget;
  const totalDdUsed = Math.max(((A.startingBalance - A.currentBalance) / A.startingBalance) * 100, 0);
  const isFailed = totalDdUsed > A.totalDrawdownLimit;
  const isOnTrack = profitPct >= pacePct;
  const daysLeft = A.daysRemaining;
  const urgent = daysLeft <= 5,
    warn = daysLeft > 5 && daysLeft <= 10;

  // trade stats
  const wins = trades.filter((t) => t.net > 0),
    losses = trades.filter((t) => t.net < 0);
  const totalPnl = trades.reduce((s, t) => s + t.pnl, 0);
  const totalComm = trades.reduce((s, t) => s + t.comm, 0);
  const winRate = trades.length ? (wins.length / trades.length) * 100 : 0;
  const avgWin = wins.length ? wins.reduce((s, t) => s + t.net, 0) / wins.length : 0;
  const avgLoss = losses.length ? Math.abs(losses.reduce((s, t) => s + t.net, 0) / losses.length) : 0;
  const pf = avgLoss > 0 ? (avgWin * wins.length) / (avgLoss * losses.length) : 0;

  // drawdown
  const today = trades.filter((t) => t.off === 0);
  const todayPnl = today.reduce((s, t) => s + t.net, 0);
  const dailyUsed = todayPnl < 0 ? Math.abs(todayPnl) : 0;
  const dailyCap = (A.startingBalance * A.dailyDrawdownLimit) / 100;
  const dailyPct = dailyCap > 0 ? (dailyUsed / dailyCap) * 100 : 0;
  const dailyRemaining = dailyCap - dailyUsed;
  const totalPct = A.totalDrawdownLimit > 0 ? Math.min((totalDdUsed / A.totalDrawdownLimit) * 100, 100) : 0;
  const totalRemaining =
    (A.startingBalance * A.totalDrawdownLimit) / 100 - (A.startingBalance - A.currentBalance);
  const gauge = (pct: number) => {
    const c = pct >= 80 ? 'var(--neg)' : pct >= 50 ? 'var(--amb)' : 'var(--pos)';
    const deep = pct >= 80 ? 'var(--neg-fill)' : pct >= 50 ? 'var(--amb-2)' : 'var(--pos-2)';
    const N = 40,
      lit = (Math.min(pct, 100) / 100) * N,
      out: GaugeTick[] = [];
    for (let i = 0; i < N; i++) {
      const a = Math.PI * (1 - (i + 0.5) / N);
      const on = i < lit,
        isCur = on && i + 1 >= lit;
      const ro = isCur ? 100 : on ? 96 : 92,
        ri = isCur ? 66 : on ? 72 : 76;
      out.push({
        x1: (100 + Math.cos(a) * ri).toFixed(1),
        y1: (104 - Math.sin(a) * ri).toFixed(1),
        x2: (100 + Math.cos(a) * ro).toFixed(1),
        y2: (104 - Math.sin(a) * ro).toFixed(1),
        col: on ? (isCur ? deep : c) : 'var(--l10)',
        w: isCur ? '3.4' : on ? '2.6' : '2.2',
        op: on ? (isCur ? '1' : (0.55 + 0.45 * (i / Math.max(1, lit))).toFixed(2)) : '1',
      });
    }
    return { ticks: out, col: c, label: pct.toFixed(0) + '%' };
  };
  const remTone = (v: number, lo: number, mid: number) =>
    v < lo ? 'var(--neg)' : v < mid ? 'var(--amb-2)' : 'var(--pos)';

  // daily buckets
  const byDay: Record<number, { pnl: number; n: number }> = {};
  trades.forEach((t) => {
    const d = (byDay[t.off] = byDay[t.off] || { pnl: 0, n: 0 });
    d.pnl += t.net;
    d.n++;
  });
  const worstDay = Math.min(0, ...Object.values(byDay).map((d) => d.pnl));
  const worstDayPct = Math.abs(worstDay / A.startingBalance) * 100;
  const dailyBreached = worstDayPct > A.dailyDrawdownLimit;

  // equity path
  let eq = A.startingBalance;
  const pts = [{ eq }];
  trades.forEach((t) => {
    eq += t.net;
    pts.push({ eq });
  });
  let peakEq = A.startingBalance,
    troughDD = 0;
  pts.forEach((p) => {
    peakEq = Math.max(peakEq, p.eq);
    troughDD = Math.max(troughDD, ((peakEq - p.eq) / A.startingBalance) * 100);
  });
  const endEq = pts[pts.length - 1]!.eq; // pts always holds the starting point

  const STATUS: Record<RuleStatus, { label: string; icon: string; fg: string; bd: string; bg: string }> = {
    passed: {
      label: 'Passed',
      icon: 'ph-check-circle',
      fg: 'var(--pos)',
      bd: 'color-mix(in srgb, var(--pos) 25%, transparent)',
      bg: 'color-mix(in srgb, var(--pos) 10%, transparent)',
    },
    ok: {
      label: 'OK',
      icon: 'ph-check-circle',
      fg: 'var(--pos)',
      bd: 'color-mix(in srgb, var(--pos) 25%, transparent)',
      bg: 'color-mix(in srgb, var(--pos) 10%, transparent)',
    },
    progress: {
      label: 'In Progress',
      icon: 'ph-activity',
      fg: 'var(--sky)',
      bd: 'color-mix(in srgb, var(--sky-fill) 25%, transparent)',
      bg: 'color-mix(in srgb, var(--sky-fill) 10%, transparent)',
    },
    warning: {
      label: 'Warning',
      icon: 'ph-warning',
      fg: 'var(--amb-2)',
      bd: 'color-mix(in srgb, var(--amb) 25%, transparent)',
      bg: 'color-mix(in srgb, var(--amb) 10%, transparent)',
    },
    failed: {
      label: 'Breached',
      icon: 'ph-x-circle',
      fg: 'var(--neg)',
      bd: 'color-mix(in srgb, var(--neg) 25%, transparent)',
      bg: 'color-mix(in srgb, var(--neg-fill) 10%, transparent)',
    },
  };
  const rules: { label: string; icon: string; status: RuleStatus; detail: string }[] = [
    {
      label: 'Profit Target',
      icon: 'ph-target',
      status: isPassed ? 'passed' : 'progress',
      detail: isPassed
        ? 'Target met — ' + cur(profitEarned) + ' of ' + cur(A.profitTarget)
        : cur(profitEarned) + ' of ' + cur(A.profitTarget) + ' (' + profitPct.toFixed(1) + '%)',
    },
    {
      label: 'Total Drawdown',
      icon: 'ph-trend-down',
      status: isFailed ? 'failed' : totalPct >= 50 ? 'warning' : 'ok',
      detail:
        totalDdUsed.toFixed(2) +
        '% below starting balance · peak-to-trough ' +
        troughDD.toFixed(2) +
        '% of a ' +
        A.totalDrawdownLimit.toFixed(1) +
        '% limit',
    },
    {
      label: 'Daily Drawdown',
      icon: 'ph-calendar-blank',
      status: dailyBreached ? 'failed' : 'ok',
      detail: dailyBreached
        ? 'Worst day: −' + worstDayPct.toFixed(2) + '% (limit: ' + A.dailyDrawdownLimit.toFixed(1) + '%)'
        : 'Worst day: −' + worstDayPct.toFixed(2) + '% — within limit',
    },
    {
      label: 'Challenge Duration',
      icon: 'ph-clock',
      status: daysLeft <= 5 ? 'warning' : 'ok',
      detail: 'Day ' + A.daysElapsed + ' of ' + A.challengeDays + ' · ' + daysLeft + ' days remaining',
    },
  ];

  const lo = Math.min(...pts.map((p) => p.eq)),
    hi = Math.max(...pts.map((p) => p.eq));
  const span = Math.max(1, hi - lo);
  const path = pts
    .map(
      (p, i) => ((i / (pts.length - 1)) * 420).toFixed(1) + ',' + (74 - ((p.eq - lo) / span) * 64).toFixed(1),
    )
    .join(' L');

  // heatmap — last 30 days
  const cells: { bg: string; title: string }[] = [];
  const peak = Math.max(1, ...Object.values(byDay).map((d) => Math.abs(d.pnl)));
  for (let off = 29; off >= 0; off--) {
    const d = byDay[off];
    const pnl = d ? d.pnl : 0,
      n = d ? d.n : 0;
    const inten = Math.min(Math.abs(pnl) / peak, 1);
    let bg = 'var(--l5)';
    if (n > 0) {
      const base = pnl > 0 ? 'var(--pos)' : 'var(--neg)';
      bg =
        inten > 0.7
          ? base
          : 'color-mix(in srgb, ' + base + ' ' + (inten > 0.35 ? 60 : 30) + '%, transparent)';
    }
    const dt = new Date(Date.UTC(2026, 6, 24) - off * 86400000);
    cells.push({
      bg,
      title:
        WEEKDAYS[dt.getUTCDay()] +
        ' ' +
        MONTHS[dt.getUTCMonth()] +
        ' ' +
        dt.getUTCDate() +
        (n ? ' · ' + sgn(pnl) + ' · ' + n + (n === 1 ? ' trade' : ' trades') : ' · No trades'),
    });
  }

  const best = trades.slice().sort((a, b) => b.net - a.net)[0];
  const worst = trades.slice().sort((a, b) => a.net - b.net)[0];
  const dayLabel = (off: number) => {
    const d = new Date(Date.UTC(2026, 6, 24) - off * 86400000);
    return MONTHS[d.getUTCMonth()] + ' ' + d.getUTCDate();
  };

  // predictive score
  const scoreTone = A.score >= 70 ? 'var(--pos)' : A.score >= 45 ? 'var(--amb-2)' : 'var(--neg)';
  const consistency = Math.round(winRate * 0.8 + 12);
  const avgDaily = profitEarned / Math.max(1, A.daysElapsed);
  const momentum = trades.slice(-8).reduce((s, t) => s + t.net, 0);

  return {
    apAccts: AP_ACCOUNTS.map((a) => ({
      label: a.nickname,
      go: () => c.set({ apAcct: a.id, apImprove: false }),
      bd: a.id === S.apAcct ? 'color-mix(in srgb, var(--amb) 30%, transparent)' : 'transparent',
      bg: a.id === S.apAcct ? 'color-mix(in srgb, var(--amb) 20%, transparent)' : 'transparent',
      fg: a.id === S.apAcct ? 'var(--amb-2)' : 'var(--muted)',
    })),
    apMulti: AP_ACCOUNTS.length > 1,
    // hero
    apName: A.nickname,
    apBroker: A.broker,
    apHeroBg: isPassed
      ? 'color-mix(in srgb, var(--pos) 10%, var(--surface))'
      : isFailed
        ? 'color-mix(in srgb, var(--neg-fill) 10%, var(--surface))'
        : 'var(--surface)',
    apHeroBd: isPassed
      ? 'color-mix(in srgb, var(--pos) 30%, transparent)'
      : isFailed
        ? 'color-mix(in srgb, var(--neg) 30%, transparent)'
        : 'var(--card-line)',
    apHeroIcon: isPassed ? 'ph-check-circle' : isFailed ? 'ph-x-circle' : 'ph-trophy',
    apHeroIconFg: isPassed ? 'var(--pos)' : isFailed ? 'var(--neg)' : 'var(--amb)',
    apHeroTileBg: isPassed
      ? 'color-mix(in srgb, var(--pos) 18%, transparent)'
      : isFailed
        ? 'color-mix(in srgb, var(--neg-fill) 18%, transparent)'
        : 'color-mix(in srgb, var(--amb) 15%, transparent)',
    apStatus: isPassed ? '✓ PASSED' : isFailed ? '✗ FAILED' : isOnTrack ? 'ON TRACK' : 'BEHIND PACE',
    apStatusFg: isPassed ? 'var(--pos)' : isFailed ? 'var(--neg)' : isOnTrack ? 'var(--sky)' : 'var(--amb-2)',
    apStatusBg: isPassed
      ? 'color-mix(in srgb, var(--pos) 14%, transparent)'
      : isFailed
        ? 'color-mix(in srgb, var(--neg-fill) 14%, transparent)'
        : isOnTrack
          ? 'color-mix(in srgb, var(--sky-fill) 14%, transparent)'
          : 'color-mix(in srgb, var(--amb) 14%, transparent)',
    apShowDays: !isPassed && !isFailed,
    apDaysLeft: daysLeft + 'd left',
    apDaysIcon: urgent ? 'ph-warning' : 'ph-clock',
    apDaysFg: urgent ? 'var(--neg)' : warn ? 'var(--amb-2)' : 'var(--muted)',
    apDaysBd: urgent
      ? 'color-mix(in srgb, var(--neg) 30%, transparent)'
      : warn
        ? 'color-mix(in srgb, var(--amb) 30%, transparent)'
        : 'var(--l8)',
    apHeroMetrics: [
      {
        label: 'Account Balance',
        value: cur(A.currentBalance),
        sub: 'Started: ' + cur(A.startingBalance),
        c: 'var(--text)',
      },
      {
        label: 'Profit Earned',
        value: sgn(profitEarned),
        sub: 'Target: ' + cur(A.profitTarget),
        c: profitEarned >= 0 ? 'var(--pos)' : 'var(--neg)',
      },
      {
        label: 'Gain %',
        value: (gainPct >= 0 ? '+' : '−') + Math.abs(gainPct).toFixed(2) + '%',
        sub: 'Required: ' + ((A.profitTarget / A.startingBalance) * 100).toFixed(1) + '%',
        c: gainPct >= 0 ? 'var(--pos)' : 'var(--neg)',
      },
      {
        label: 'Challenge Day',
        value: A.daysElapsed + ' / ' + A.challengeDays,
        sub: 'Started ' + A.challengeStart,
        c: 'var(--text)',
      },
    ],
    apProfitPct: profitPct.toFixed(1) + '%',
    // scaleX, not width — see `pfProgScale`. Clamped at both ends: a challenge past its target is a full bar, not
    // one that overflows its track.
    apProfitScale: ((Math.min(Math.max(profitPct, 0), 100) / 100) * (c.S.intro ?? 1)).toFixed(4),
    apProfitFg: profitPct >= 100 ? 'var(--pos)' : profitPct >= 60 ? 'var(--sky)' : 'var(--text)',
    apProfitBar:
      profitPct >= 100
        ? 'var(--pos)'
        : profitPct >= 60
          ? 'var(--sky-fill)'
          : profitPct >= 30
            ? 'var(--amb)'
            : 'var(--muted2)',
    apPaceLeft: pacePct.toFixed(1) + '%',
    apDayEnd: 'Day ' + A.challengeDays,
    // stat cards
    apStats: [
      {
        icon: 'ph-chart-bar',
        label: 'Total Trades',
        value: String(trades.length),
        sub: wins.length + 'W / ' + losses.length + 'L',
        c: 'var(--text)',
      },
      {
        icon: 'ph-percent',
        label: 'Win Rate',
        value: winRate.toFixed(1) + '%',
        sub: '',
        c: winRate >= 50 ? 'var(--pos)' : 'var(--neg)',
      },
      {
        icon: 'ph-lightning',
        label: 'Profit Factor',
        value: pf.toFixed(2),
        sub: '',
        c: pf >= 1.5 ? 'var(--pos)' : pf >= 1 ? 'var(--amb-2)' : 'var(--neg)',
      },
      {
        icon: 'ph-currency-dollar',
        label: 'Net P&L',
        value: sgn(totalPnl - totalComm),
        sub: 'after ' + cur(totalComm) + ' commission',
        c: totalPnl - totalComm >= 0 ? 'var(--pos)' : 'var(--neg)',
      },
    ],
    // drawdown
    apDailyGauge: gauge(dailyPct),
    apTotalGauge: gauge(totalPct),
    apDailySub: cur(dailyUsed) + ' / ' + cur(dailyCap),
    apTotalSub:
      totalDdUsed.toFixed(2) +
      '% / ' +
      A.totalDrawdownLimit.toFixed(1) +
      '% · peak-to-trough ' +
      troughDD.toFixed(2) +
      '%',
    apDailyRemaining: cur(Math.max(dailyRemaining, 0)) + ' remaining today',
    apDailyRemFg: remTone(dailyRemaining, 200, 500),
    apTotalRemaining: cur(Math.max(totalRemaining, 0)) + ' buffer left',
    apTotalRemFg: remTone(totalRemaining, 500, 1500),
    // data-lint-allow: drawdown zone legend copy
    apZones: [
      { label: 'Safe Zone', range: '0–50%', c: 'var(--pos)' },
      { label: 'Caution', range: '50–80%', c: 'var(--amb)' },
      { label: 'Danger', range: '80–100%', c: 'var(--neg)' },
    ],
    // rules
    apRules: rules.map((r) => {
      const c = STATUS[r.status];
      return {
        label: r.label,
        icon: r.icon,
        detail: r.detail,
        fg: c.fg,
        bd: c.bd,
        bg: c.bg,
        statusIcon: c.icon,
        statusLabel: c.label,
      };
    }),
    // equity
    apEqPath: 'M' + path,
    apEqFill: 'M' + path + ' L420,74 L0,74 Z',
    apEqStroke: profitEarned >= 0 ? 'var(--pos)' : 'var(--neg)',
    apEqCount: trades.length + ' closed trades',
    apEqStart: 'Start: ' + cur(A.startingBalance),
    apEqCurrent: 'Current: ' + cur(endEq),
    // predictive
    apScore: String(A.score),
    apScoreLabel: A.scoreLabel,
    apScoreTicks: (() => {
      const deep = A.score >= 70 ? 'var(--pos-2)' : A.score >= 45 ? 'var(--amb-2)' : 'var(--neg-fill)';
      const N = 40,
        lit = (Math.min(A.score, 100) / 100) * N,
        out: GaugeTick[] = [];
      for (let i = 0; i < N; i++) {
        const a = Math.PI * (1 - (i + 0.5) / N);
        const on = i < lit,
          isCur = on && i + 1 >= lit;
        const ro = isCur ? 100 : on ? 96 : 92,
          ri = isCur ? 66 : on ? 72 : 76;
        out.push({
          x1: (100 + Math.cos(a) * ri).toFixed(1),
          y1: (104 - Math.sin(a) * ri).toFixed(1),
          x2: (100 + Math.cos(a) * ro).toFixed(1),
          y2: (104 - Math.sin(a) * ro).toFixed(1),
          col: on ? (isCur ? deep : scoreTone) : 'var(--l10)',
          w: isCur ? '3.4' : on ? '2.6' : '2.2',
          op: on ? (isCur ? '1' : (0.55 + 0.45 * (i / Math.max(1, lit))).toFixed(2)) : '1',
        });
      }
      return out;
    })(),
    apScoreFg: scoreTone,
    apConfidence: A.confidence + '% confidence',
    apLowSample: trades.length < 10,
    apLowSampleNote:
      'Low sample size — score improves as more trades are recorded (' + trades.length + '/10)',
    apSummary: A.summary,
    apProjected: A.projected,
    apHasBest: !!A.best,
    apBestDate: A.best ? '↑ Best: ' + A.best : '',
    apHasWorstDate: !!A.worst,
    apWorstDate: A.worst ? '↓ Worst: ' + A.worst : '',
    // apNoProject: false — the reference returns this constant, but no generated view binds it (absent from ViewModel), so it is omitted.
    apStrengths: [
      'Profit pace is ' +
        (pacePct > 0 ? (profitPct / pacePct).toFixed(1) : '—') +
        '× the rate the deadline requires',
      'Peak-to-trough drawdown has reached ' +
        troughDD.toFixed(2) +
        '% against a ' +
        A.totalDrawdownLimit.toFixed(1) +
        '% limit',
      'No daily-loss breach across ' +
        A.daysElapsed +
        ' sessions — worst day −' +
        worstDayPct.toFixed(2) +
        '%',
    ]
      .concat(A.strengths.filter((s) => !/drawdown|%/i.test(s)))
      .map((t) => ({ t })),
    apRisks: A.risks.map((t) => ({ t })),
    apKeyMetrics: [
      { label: 'Win Rate', value: winRate.toFixed(0) + '%', c: 'var(--text)' },
      { label: 'Profit Factor', value: pf.toFixed(2), c: 'var(--text)' },
      { label: 'Consistency', value: consistency + '%', c: 'var(--text)' },
      { label: 'Avg Daily', value: cur(avgDaily), c: 'var(--text)' },
      { label: 'Momentum', value: sgn(momentum), c: momentum >= 0 ? 'var(--pos)' : 'var(--neg)' },
      {
        label: 'DD Safety',
        value: (A.totalDrawdownLimit - totalDdUsed).toFixed(1) + '%',
        c: A.totalDrawdownLimit - totalDdUsed > 3 ? 'var(--pos)' : 'var(--neg)',
      },
    ],
    apScoreHistory: (() => {
      const hist: number[] = [];
      let v = A.score - 18;
      for (let i = 0; i < 8; i++) {
        v = Math.max(20, Math.min(96, v + ((A.seed * (i + 3)) % 11) - 3));
        hist.push(i === 7 ? A.score : Math.round(v));
      }
      const first = hist[0]!; // eight snapshots are always pushed
      const lo = Math.min(...hist),
        hi = Math.max(...hist),
        span = Math.max(1, hi - lo);
      return {
        path:
          'M' +
          hist
            .map(
              (s, i) =>
                ((i / (hist.length - 1)) * 300).toFixed(1) + ',' + (34 - ((s - lo) / span) * 28).toFixed(1),
            )
            .join(' L'),
        lo: String(lo),
        hi: String(hi),
        span: hist.length + ' snapshots',
        stroke: A.score >= 70 ? 'var(--pos)' : A.score >= 45 ? 'var(--amb)' : 'var(--neg)',
        delta: (A.score - first >= 0 ? '+' : '−') + Math.abs(A.score - first) + ' since first snapshot',
        deltaFg: A.score - first >= 0 ? 'var(--pos)' : 'var(--neg)',
      };
    })(),
    apImproveOpen: S.apImprove,
    apImproveIcon: S.apImprove ? 'ph-caret-up' : 'ph-caret-down',
    apImproveToggle: () => c.set((s) => ({ apImprove: !s.apImprove })),
    apImprovements: A.risks.map((r, i) => ({ n: String(i + 1), t: r })),
    // heatmap
    apCells: cells,
    // best / worst — the reference returns null when there is no trade; the generated shape wants an object, and the
    // view only reads it behind apHasBestTrade/apHasWorstTrade, so an empty placeholder stands in for null.
    apBest: best
      ? {
          sym: best.sym,
          meta: best.dir.toUpperCase() + ' · ' + best.lots.toFixed(2) + ' lots · ' + dayLabel(best.off),
          pnl: sgn(best.net),
        }
      : { sym: '', meta: '', pnl: '' },
    apWorst: worst
      ? {
          sym: worst.sym,
          meta: worst.dir.toUpperCase() + ' · ' + worst.lots.toFixed(2) + ' lots · ' + dayLabel(worst.off),
          pnl: sgn(worst.net),
        }
      : { sym: '', meta: '', pnl: '' },
    apHasBestTrade: !!best,
    apHasWorstTrade: !!worst,
    // trade log
    apLogCount: trades.length + ' trades',
    apLog: trades
      .slice()
      .sort((a, b) => a.off - b.off)
      .slice(0, 30)
      .map((t) => ({
        date: dayLabel(t.off),
        sym: t.sym,
        dir: t.dir.toUpperCase(),
        dirFg: t.dir === 'buy' ? 'var(--pos)' : 'var(--neg)',
        dirBg:
          t.dir === 'buy'
            ? 'color-mix(in srgb, var(--pos) 14%, transparent)'
            : 'color-mix(in srgb, var(--neg-fill) 14%, transparent)',
        lots: t.lots.toFixed(2) + 'L',
        net: sgn(t.net),
        netFg: t.net >= 0 ? 'var(--pos)' : 'var(--neg)',
        dot: t.net >= 0 ? 'var(--pos)' : 'var(--neg)',
      })),
  } satisfies Partial<ViewModel>;
}
